import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import "katex/dist/katex.min.css";
import katex from "katex";
// KaTeX描画コンポーネント
const Tex: React.FC<{ tex: string; block?: boolean }> = ({ tex, block }) => {
  try {
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: katex.renderToString(tex, { displayMode: !!block }),
        }}
        style={
          block ? { display: "block", margin: "1em 0", overflowX: "auto" } : {}
        }
      />
    );
  } catch {
    return <span style={{ color: "red", background: "#fff0f0" }}>{tex}</span>;
  }
};

/* ---------- 共通スタイル定義 ---------- */
const inlineCodeStyle: React.CSSProperties = {
  backgroundColor: "#fafafa",
  color: "#383a42",
  padding: "0.2em 0.4em",
  margin: 0,
  fontSize: "85%",
  borderRadius: 6,
  fontFamily:
    'SFMono-Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace',
  border: "1px solid #e1e1e1",
  wordBreak: "break-all",
  overflowWrap: "break-word",
};

const linkBaseColor = "#0366d6";

/* ---------- ユーティリティ ---------- */
const escapeHtml = (str: string) =>
  str
    ? str
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;")
    : "";

/* ---------- Spoiler ---------- */
const Spoiler: React.FC<{
  children: React.ReactNode;
  containsCodeBlock?: boolean;
}> = ({ children, containsCodeBlock }) => {
  const [open, setOpen] = useState(false);
  const base: React.CSSProperties = {
    backgroundColor: open ? "transparent" : "#c6cdd7ff",
    padding: "0.1em 0.2em",
    borderRadius: 4,
    cursor: open ? "auto" : "pointer",
    display: containsCodeBlock ? "block" : "inline-block",
    position: "relative",
    transition: "background-color 0.15s",
    overflow: "hidden",
  };
  const hiddenStyle: React.CSSProperties = open
    ? {}
    : {
        color: "transparent",
        textShadow: "none",
        pointerEvents: "none",
        userSelect: "none",
      };

  return (
    <span
      onClick={() => !open && setOpen(true)}
      style={base}
      title={open ? "" : "クリックして表示"}
    >
      <span style={hiddenStyle} aria-hidden={!open}>
        {children}
      </span>
      {!open && (
        <span
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#888",
            fontWeight: 600,
            pointerEvents: "none",
            userSelect: "none",
            fontSize: "90%",
            backgroundColor: "#c6cdd7ff",
            zIndex: 3,
          }}
        >
          ネタバレ
        </span>
      )}
    </span>
  );
};

/* ---------- Link (外部 / 内部) ---------- */
const StyledLink: React.FC<{
  href?: string;
  to?: string;
  children: React.ReactNode;
}> = ({ href, to, children }) => {
  const [hover, setHover] = useState(false);
  const style: React.CSSProperties = {
    color: linkBaseColor,
    textDecoration: hover ? "underline" : "none",
  };
  const common = {
    style,
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };
  if (to)
    return (
      <Link to={to} {...common}>
        {children}
      </Link>
    );
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...common}>
      {children}
    </a>
  );
};

/* ---------- インラインパース ---------- */
/*
 対応:
 `code` / **bold** / ~~strike~~ / ||spoiler|| / #hashtag / @mention / [text](url) / 自動URL / 改行
 優先順位: 明確に囲いのあるトークン -> ハッシュ/メンション/リンク -> URL -> プレーン
*/
const inlineTokenRegex =
  /(`[^`]+`)|(\*\*.*?\*\*)|(~~.*?~~)|(\|\|.*?\|\|)|(__.*?__)|(#[\w\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]+)|(@[a-z\d_-]+)|(\[.*?\]\(.*?\))/gi;
const autoUrlRegex = /(https?:\/\/[^\s]+)/g;
const inlineTexRegex = /\$([^$\n]+)\$/g;

function parseInline(text: string): React.ReactNode {
  if (!text) return null;

  // インラインTeXを先に分割
  const texParts: (string | React.ReactNode)[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = inlineTexRegex.exec(text)) !== null) {
    if (m.index > last) texParts.push(text.slice(last, m.index));
    texParts.push(<Tex key={"tex-" + texParts.length} tex={m[1]} />);
    last = inlineTexRegex.lastIndex;
  }
  if (last < text.length) texParts.push(text.slice(last));

  // 既存のインラインパースを各パートに適用
  return texParts.flatMap((part, idx) => {
    if (typeof part !== "string") return part;
    const parts = part.split(inlineTokenRegex).filter(Boolean);
    return parts.map((part2, idx2) => {
      // インラインコード
      if (part2.startsWith("`") && part2.endsWith("`")) {
        return (
          <code key={idx + "-" + idx2} style={inlineCodeStyle}>
            {escapeHtml(part2.slice(1, -1))}
          </code>
        );
      }
      // 太字
      if (part2.startsWith("**") && part2.endsWith("**")) {
        return (
          <strong key={idx + "-" + idx2}>
            {parseInline(part2.slice(2, -2))}
          </strong>
        );
      }
      // 打ち消し
      if (part2.startsWith("~~") && part2.endsWith("~~")) {
        return <s key={idx + "-" + idx2}>{parseInline(part2.slice(2, -2))}</s>;
      }
      // 下線
      if (part2.startsWith("__") && part2.endsWith("__")) {
        return <u key={idx + "-" + idx2}>{parseInline(part2.slice(2, -2))}</u>;
      }
      // スポイラー
      if (part2.startsWith("||") && part2.endsWith("||")) {
        return (
          <Spoiler key={idx + "-" + idx2}>
            {parseInline(part2.slice(2, -2))}
          </Spoiler>
        );
      }
      // ハッシュタグ
      if (part2.startsWith("#")) {
        const tag = part2.slice(1);
        return (
          <StyledLink key={idx + "-" + idx2} to={`/search?q=${tag}`}>
            {part2}
          </StyledLink>
        );
      }
      // メンション
      if (part2.startsWith("@")) {
        const mention = part2.slice(1);
        return (
          <StyledLink key={idx + "-" + idx2} to={`/users/${mention}`}>
            {part2}
          </StyledLink>
        );
      }
      // [text](url)
      const m = part2.match(/^\[(.*?)\]\((.*?)\)$/);
      if (m) {
        const [, t, url] = m;
        const isSafeUrl = /^https?:\/\//i.test(url);
        return isSafeUrl ? (
          <span
            key={idx + "-" + idx2}
            style={{ display: "inline-block", verticalAlign: "top" }}
          >
            <StyledLink href={url}>{parseInline(t)}</StyledLink>
            <div>
              <OgpCard url={url} />
            </div>
          </span>
        ) : (
          <span
            key={idx + "-" + idx2}
            style={{ color: "#888", textDecoration: "line-through" }}
          >
            {parseInline(t)}
          </span>
        );
      }
      // 自動URL分割
      const urls = part2.match(autoUrlRegex);
      if (urls) {
        const segments = part2.split(autoUrlRegex);
        return segments.map((seg, i) =>
          autoUrlRegex.test(seg) ? (
            <span
              key={`${idx + "-" + idx2}-u-${i}`}
              style={{ display: "inline-block", verticalAlign: "top" }}
            >
              <StyledLink href={seg}>{seg}</StyledLink>
              <div>
                <OgpCard url={seg} />
              </div>
            </span>
          ) : (
            <React.Fragment key={`${idx + "-" + idx2}-t-${i}`}>
              {seg.split("\n").map((line, li, arr) => (
                <React.Fragment key={`${idx + "-" + idx2}-t-${i}-l-${li}`}>
                  {escapeHtml(line)}
                  {li < arr.length - 1 && <br />}
                </React.Fragment>
              ))}
            </React.Fragment>
          )
        );
      }
      // プレーンテキスト + 改行
      return part2.split("\n").map((line, li, arr) => (
        <React.Fragment key={`${idx + "-" + idx2}-l-${li}`}>
          <span style={{ wordBreak: "break-all", overflowWrap: "break-word" }}>
            {line}
          </span>
          {li < arr.length - 1 && <br />}
        </React.Fragment>
      ));
    });
  });
}

/* ---------- コードブロック + コピー ---------- */
const CodeBlockWithCopy: React.FC<{ code: string; language: string }> = ({
  code,
  language,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 5000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={{ position: "relative", margin: "1em 0" }}>
      <div
        style={{ position: "relative", overflow: "hidden", borderRadius: 8 }}
      >
        <span
          style={{
            position: "absolute",
            top: 8,
            left: 12,
            zIndex: 1,
            color: "#888",
            fontSize: 14,
            userSelect: "none",
            fontFamily: "sans-serif",
          }}
        >
          {language}
        </span>
        <div
          style={{
            // SyntaxHighlighter がレンダリングする pre/code に確実にスタイルを当てるための wrapper
            width: "100%",
            overflow: "hidden",
          }}
        >
          <SyntaxHighlighter
            language={language}
            style={oneLight}
            PreTag="div"
            wrapLongLines={true}
            codeTagProps={{
              style: {
                wordBreak: "break-all",
                overflowWrap: "break-word",
                whiteSpace: "pre-wrap",
              },
            }}
            customStyle={{
              margin: 0,
              paddingTop: 32,
              borderRadius: 8,
            }}
            lineProps={{ style: { wordBreak: "break-all" } }}
          >
            {code.trimEnd()}
          </SyntaxHighlighter>
        </div>
        <button
          onClick={handleCopy}
          aria-label="コードをコピー"
          title={copied ? "コピー済み" : "コピー"}
          style={{
            position: "absolute",
            top: 8,
            right: 12,
            zIndex: 1,
            background: copied ? "#d1fae5" : "#f3f4f6",
            border: "1px solid #ccc",
            borderRadius: 4,
            width: 28,
            height: 28,
            padding: 0,
            fontSize: 16,
            cursor: "pointer",
            transition: "background 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 18,
              height: 18,
              position: "relative",
            }}
          >
            <span
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: copied ? 1 : 0,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="#34d399" />
                <path
                  d="M6 10.5l3 3 5-5"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: copied ? 0 : 1,
                transition: "opacity 0.2s ease-in-out",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <rect
                  x="7"
                  y="7"
                  width="9"
                  height="9"
                  rx="2"
                  stroke="#555"
                  strokeWidth="2"
                />
                <rect
                  x="4"
                  y="4"
                  width="9"
                  height="9"
                  rx="2"
                  stroke="#bbb"
                  strokeWidth="2"
                  fill="#fff"
                />
              </svg>
            </span>
          </span>
        </button>
      </div>
    </div>
  );
};

/* ---------- ブロックパース（コードブロック） ---------- */
const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
const blockTexRegex = /\$\$([\s\S]+?)\$\$/g;

/* ---------- コードブロック保護パース ---------- */
export function parseCustomMarkdown(text: string): React.ReactNode {
  if (!text) return null;

  // ステップ1: コードブロックを抽出してプレースホルダーに置き換え
  const codeBlocks: Array<{ code: string; language: string }> = [];
  const textWithPlaceholders = text.replace(
    codeBlockRegex,
    (match, lang, code) => {
      const index = codeBlocks.length;
      codeBlocks.push({ code: code || "", language: lang || "text" });
      return `___CODE_BLOCK_${index}___`;
    }
  );

  // ステップ2: プレースホルダーで置き換えたテキストでスポイラーを処理
  const spoilerRegex = /\|\|([\s\S]+?)\|\|/g;
  const out: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = spoilerRegex.exec(textWithPlaceholders)) !== null) {
    if (m.index > last) {
      out.push(
        ...restoreCodeBlocks(
          textWithPlaceholders.slice(last, m.index),
          codeBlocks
        )
      );
    }
    const containsCodeBlock = /___CODE_BLOCK_\d+___/.test(m[1]);
    out.push(
      <Spoiler key={out.length} containsCodeBlock={containsCodeBlock}>
        {restoreCodeBlocks(m[1], codeBlocks)}
      </Spoiler>
    );
    last = spoilerRegex.lastIndex;
  }
  if (last < textWithPlaceholders.length) {
    out.push(
      ...restoreCodeBlocks(textWithPlaceholders.slice(last), codeBlocks)
    );
  }

  return <>{out}</>;
}

/* ---------- コードブロックプレースホルダー復元 ---------- */
function restoreCodeBlocks(
  text: string,
  codeBlocks: Array<{ code: string; language: string }>
): React.ReactNode[] {
  if (!text) return [];

  const placeholderRegex = /___CODE_BLOCK_(\d+)___/g;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = placeholderRegex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      const nonCodePart = parseBlocksWithoutCodeBlocks(
        text.slice(lastIndex, m.index)
      );
      if (nonCodePart) nodes.push(nonCodePart);
    }
    const blockIndex = parseInt(m[1], 10);
    const block = codeBlocks[blockIndex];
    if (block) {
      nodes.push(
        <CodeBlockWithCopy
          key={nodes.length}
          code={block.code}
          language={block.language}
        />
      );
    }
    lastIndex = placeholderRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    const remaining = parseBlocksWithoutCodeBlocks(text.slice(lastIndex));
    if (remaining) nodes.push(remaining);
  }

  return nodes;
}

/* ---------- ブロックパース（コードブロック除く） ---------- */
function parseBlocksWithoutCodeBlocks(text: string): React.ReactNode {
  if (!text) return null;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  // ブロックTeX
  while ((m = blockTexRegex.exec(text)) !== null) {
    if (m.index > lastIndex) {
      nodes.push(
        <React.Fragment key={nodes.length}>
          {parseInline(text.slice(lastIndex, m.index))}
        </React.Fragment>
      );
    }
    nodes.push(<Tex key={nodes.length} tex={m[1]} block />);
    lastIndex = blockTexRegex.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(
      <React.Fragment key={nodes.length}>
        {parseInline(text.slice(lastIndex))}
      </React.Fragment>
    );
  }
  return <>{nodes}</>;
}

/* ---------- エクスポートコンポーネント ---------- */
const CustomMarkdown: React.FC<{ children: string }> = ({ children }) => (
  <>{parseCustomMarkdown(children)}</>
);

export default CustomMarkdown;

// OGPカードコンポーネント
const OgpCard: React.FC<{ url: string }> = ({ url }) => {
  const [data, setData] = React.useState<any>(null);
  const [error, setError] = React.useState(false);
  if (error) return null;
  if (!data)
    return (
      <div style={{ margin: "1em 0", color: "#888" }}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      </div>
    );
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #eee",
        borderRadius: 8,
        textDecoration: "none",
        color: "#222",
        margin: "1em 0",
        maxWidth: 480,
        background: "#fafbfc",
      }}
    >
      {data.image?.url && (
        <img
          src={data.image.url}
          alt=""
          style={{
            width: 80,
            height: 80,
            objectFit: "cover",
            borderRadius: 8,
            marginRight: 12,
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 16,
            marginBottom: 4,
            whiteSpace: "normal",
            overflowWrap: "break-word",
            wordBreak: "break-all",
          }}
        >
          {data.title || url}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "#555",
            marginBottom: 2,
            overflowWrap: "break-word",
            wordBreak: "break-all",
            whiteSpace: "normal",
          }}
        >
          {data.description}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "#888",
            overflowWrap: "break-word",
            wordBreak: "break-all",
          }}
        >
          {data.url}
        </div>
      </div>
    </a>
  );
};
